import { requireService } from '@profgeosoft/di-ez';
import { action, flow, makeObservable, observable } from 'mobx';

import { storageAgent } from 'src/api/agent';
import { hasValue } from 'src/packages/utils/has-value';
import { createPromiseController, type TPromiseController } from 'src/packages/utils/promise-controller';

import type { AxiosInstance } from 'axios';
import type { IMainServices } from 'src/app/types';
import type { MainDirectoryService } from 'src/services/directory-service/main-directory-service';
import type { TActionButtonView, TControlView } from 'src/services/directory-service/types';
import type { INotificationsService } from 'src/services/notifications-service';

type TSerializedRoomsParams = {
  source: Record<string, unknown>;
  target: Record<string, unknown>;
};

export class CopyToAnotherRoomStore {
  private readonly notifications: INotificationsService;
  private readonly agent: AxiosInstance;
  private readonly directoryName: string;
  private readonly directory: MainDirectoryService;

  readonly view: TActionButtonView;
  readonly generalControlViews: TControlView[];
  readonly generalControlsData: Record<string, unknown>;
  readonly requiredFields: string[];

  @observable sourceControlsData: Record<string, unknown> = {};
  @observable targetControlsData: Record<string, unknown> = {};

  @observable sourceDataErrors: Record<string, string> = {};
  @observable targetDataErrors: Record<string, string> = {};

  @observable private confirmPromiseController: TPromiseController<boolean> | null = null;
  @observable isOpened = false;
  @observable isConfirmModalOpened = false;
  @observable isLoading = false;

  constructor(mainServices: IMainServices, directory: MainDirectoryService, view: TActionButtonView) {
    this.notifications = requireService('notificationsService');
    this.view = view;
    this.directory = directory;
    this.requiredFields = directory.view.required;

    this.directoryName = directory.directoryName;
    this.generalControlsData = directory.generalDataValues;
    this.agent = storageAgent;

    makeObservable(this);
  }

  private serializeControlsValues(): TSerializedRoomsParams {
    return { source: this.sourceControlsData, target: this.targetControlsData };
  }

  @action.bound
  setIsOpened(isOpened: boolean): void {
    this.isOpened = isOpened;
  }

  @action.bound
  setIsConfirmModalOpened(isOpened: boolean): void {
    this.isConfirmModalOpened = isOpened;
  }

  @action.bound
  prefillControls(): void {
    const sourceData: Record<string, unknown> = {};

    this.view.copyModal.sourceRoom.forEach((controlView) => {
      const correspondingGeneralControlValue = this.generalControlsData[controlView.fieldId];

      if (hasValue(correspondingGeneralControlValue)) {
        sourceData[controlView.fieldId] = correspondingGeneralControlValue;
      }
    });

    this.sourceControlsData = sourceData;
  }

  @action.bound
  onFieldValueChange(type: 'source' | 'target', fieldId: string, value: unknown): void {
    if (type === 'source') {
      const { [fieldId]: test, ...rest } = this.sourceDataErrors;

      this.sourceDataErrors = rest;

      this.sourceControlsData = { ...this.sourceControlsData, [fieldId]: value };
      return;
    }

    const { [fieldId]: test, ...rest } = this.targetDataErrors;

    this.targetDataErrors = rest;

    this.targetControlsData = { ...this.targetControlsData, [fieldId]: value };
  }

  @action.bound
  resetControls(): void {
    this.sourceControlsData = {};
    this.targetControlsData = {};
  }

  @action.bound
  validate(): boolean {
    let isAllRequiredControlsFilledIn = true;

    const checkIsRequiredIsNotEmpty = (fieldId: string, value: unknown): boolean => {
      const isValid = this.requiredFields.includes(fieldId) ? hasValue(value) : true;

      return isValid;
    };

    this.view.copyModal.sourceRoom.forEach((fieldView) => {
      isAllRequiredControlsFilledIn = checkIsRequiredIsNotEmpty(
        fieldView.fieldId,
        this.sourceControlsData[fieldView.fieldId],
      );
    });

    this.view.copyModal.targetRoom.forEach((fieldView) => {
      isAllRequiredControlsFilledIn = checkIsRequiredIsNotEmpty(
        fieldView.fieldId,
        this.targetControlsData[fieldView.fieldId],
      );
    });

    if (!isAllRequiredControlsFilledIn) {
      this.notifications.showErrorMessageT('directory:components.copyToAnotherRoom.requiredValues');
      return false;
    }

    /** Параметры комнаты-донора и целевой комнаты не должны быть идентичными */
    let identicalPairs = 0;
    let isInvalidPairsPresented = false;

    for (let i = 0; i < this.view.copyModal.sourceRoom.length; i++) {
      const sourceRoomFieldId = this.view.copyModal.sourceRoom[i]?.fieldId;

      if (!sourceRoomFieldId) {
        console.error('fieldId is not presented');
        continue;
      }

      if (this.sourceControlsData[sourceRoomFieldId] === this.targetControlsData[sourceRoomFieldId]) {
        identicalPairs += 1;
      }

      /** Контролы должны заполняться попарно. Т.е. если заполнен контрол, отвечающий за "Модель" комнаты-донора, то контрол "Модель" целевой комнаты тоже должен быть заполнен */
      if (
        hasValue(this.sourceControlsData[sourceRoomFieldId]) !== hasValue(this.targetControlsData[sourceRoomFieldId])
      ) {
        isInvalidPairsPresented = true;

        if (!hasValue(this.sourceControlsData[sourceRoomFieldId])) {
          this.sourceDataErrors[sourceRoomFieldId] = 'filed should be filled in';
        } else {
          this.targetDataErrors[sourceRoomFieldId] = 'filed should be filled in';
        }
      }
    }

    if (identicalPairs === this.view.copyModal.sourceRoom.length) {
      this.notifications.showErrorMessageT('directory:components.copyToAnotherRoom.identicalValues');
      return false;
    }

    if (isInvalidPairsPresented) {
      this.notifications.showErrorMessageT('directory:components.copyToAnotherRoom.invalidPairs');
      return false;
    }

    return true;
  }

  @action.bound
  confirmCopying(): void {
    this.confirmPromiseController?.resolve(true);
    this.setIsConfirmModalOpened(false);
  }

  @action.bound
  denyCopying(): void {
    this.confirmPromiseController?.resolve(false);
    this.setIsConfirmModalOpened(false);
  }

  @flow.bound
  async *applyCopying() {
    if (!this.validate()) {
      return;
    }

    this.confirmPromiseController = createPromiseController();
    this.setIsConfirmModalOpened(true);

    const res = await this.confirmPromiseController;
    yield;

    if (!res) {
      return;
    }

    this.isLoading = true;

    const data = this.serializeControlsValues();

    try {
      await this.agent.post(`/view/action/copy/${this.directoryName}`, data);
      yield;

      this.setIsOpened(false);
      this.resetControls();
    } catch (e) {
      yield;

      console.error(e);
    } finally {
      this.isLoading = false;
    }
  }
}
